/**
 * Seasonal merchandising windows for the home page.
 *
 * Determines which holiday the home page hero/shelves should lean into based
 * on the current date. Halloween buying ramps in August and peaks Sept-Oct;
 * Christmas takes over the day after Halloween and runs through early January.
 *
 * The windows are deliberately simple (month/day comparisons, no timezone
 * gymnastics): this is merchandising copy selection, not scheduling.
 */

export type ActiveSeason = 'halloween' | 'christmas' | 'default';

// Month is 0-based (Jan = 0), encoded as month * 100 + day so a single
// integer comparison covers the window. Christmas wraps the year boundary.
const HALLOWEEN_START = 7 * 100 + 1; // Aug 1
const HALLOWEEN_END = 9 * 100 + 31; // Oct 31
const CHRISTMAS_START = 10 * 100 + 1; // Nov 1
const CHRISTMAS_END = 11 * 100 + 31; // Dec 31
// The Christmas window wraps the year boundary and ends Jan 6 (Epiphany).
// January dates encode as monthDay 1-6, so <= 6 means Jan 1-6 exactly.
const CHRISTMAS_JANUARY_END = 6;

/**
 * Get the season the home page should currently merchandise.
 *
 * - 'halloween': Aug 1 through Oct 31 inclusive (buying season ramp + peak)
 * - 'christmas': Nov 1 through Jan 6 inclusive (runs past the holiday itself)
 * - 'default': anything else (Jan 7 - Jul 31)
 *
 * @param date - Override for testing; defaults to the current date.
 */
export function getActiveSeason(date: Date = new Date()): ActiveSeason {
  const monthDay = date.getMonth() * 100 + date.getDate();

  if (monthDay >= HALLOWEEN_START && monthDay <= HALLOWEEN_END) {
    return 'halloween';
  }

  if (
    (monthDay >= CHRISTMAS_START && monthDay <= CHRISTMAS_END) ||
    monthDay <= CHRISTMAS_JANUARY_END
  ) {
    return 'christmas';
  }

  return 'default';
}
